import React from "react";
import Skeleton from "react-loading-skeleton";

import { SITE_PRIVACY_URL, SITE_TERMS_URL } from "./config";
import { track } from "./beacon";
import { initials } from "./mark";
import { readViaFromUrl, viaFields } from "./qr-tag";
import { currentSubdomain } from "./subdomain";
import {
  doneLines,
  LIMITS,
  readTextsPage,
  signUp,
  TEXTS_WORDS,
  US_MOBILE_PATTERN,
  type TextsPageState,
} from "./texts";
import type { Client } from "./App";

const T = TEXTS_WORDS;

/** THE SHOP'S OWN PAGE FOR TEXTS (TW-0320 slice 2; texts.ts has the rules and Chris's words). On a phone first: the shop's
 *  mark and name, a first and last name, a mobile number, the ONE box whose words are the door's, the button, and the two
 *  policies a carrier's reviewer looks for at the point of consent. Nothing else is asked.
 *
 *  The shop's look is the form's own: the mark from the static config or the API's /shop (App.tsx), the lettermark where
 *  there is none. The NAME in the title is the door's once it answers (the name the box's words carry), the form's until then.
 *  The browser checks every box first (required, the door's own pattern for a US mobile, the waiver form's lengths); the
 *  door's sentence is the backstop, shown as it comes. */
export default function TextsPage({ client }: { client: Client }) {
  const subdomain = React.useMemo(currentSubdomain, []);
  // which printed code brought this load: the press keeps it on the client's record, the beacon on its rows
  const via = React.useMemo(() => readViaFromUrl(), []);
  const [page, setPage] = React.useState<TextsPageState | null>(null);
  const [first, setFirst] = React.useState("");
  const [last, setLast] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [agreed, setAgreed] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [refusal, setRefusal] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<{ first: string } | null>(null);

  const load = React.useCallback(() => {
    let live = true;
    setPage(null);
    readTextsPage(subdomain).then((answer) => {
      if (live) setPage(answer);
    });
    return () => {
      live = false;
    };
  }, [subdomain]);
  React.useEffect(() => load(), [load]);

  const name = (page?.state === "open" && page.name) || client.name || "";
  const meta = () => ({ shop: subdomain, lang: "en", ...viaFields() });

  // what this load turned out to be, once (beacon.ts holds both names to once a visit)
  React.useEffect(() => {
    if (!page) return;
    if (page.state === "open") track("texts_open", meta());
    else track("texts_closed", { ...meta(), reason: page.state === "closed" ? "not_open" : "unreachable" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending || page?.state !== "open") return;
    setSending(true);
    setRefusal(null);
    track("texts_start", meta());
    const answer = await signUp(subdomain, { first, last, phone }, page.words, via);
    setSending(false);
    if (answer.ok) {
      track("texts_done", meta());
      setDone({ first: first.trim() });
      return;
    }
    track("texts_refused", { ...meta(), reason: answer.code, status: answer.status });
    setRefusal(answer.message);
    // the words moved under the page: draw the new ones, and the box asks again
    if (answer.code === "words_changed") {
      setAgreed(false);
      readTextsPage(subdomain).then((fresh) => setPage(fresh));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-6">
      <main className="mx-auto w-full max-w-md bg-white px-5 py-6 shadow sm:rounded-lg" data-testid="texts-page">
        {client.public_data?.logo_url ? (
          <img src={client.public_data.logo_url} alt="" className="mx-auto mb-3" style={{ width: 160 }} />
        ) : (
          <div
            aria-hidden
            className={`mx-auto mb-3 flex items-center justify-center rounded-2xl font-bold select-none ${client.placeholder ? "bg-gray-200" : "bg-black text-white"}`}
            style={{ width: 72, height: 72, fontSize: 28, letterSpacing: 1 }}
          >
            {client.placeholder || !name ? "" : initials(name)}
          </div>
        )}
        <h1 className="text-center text-2xl font-bold">{T.title(name)}</h1>

        {done ? (
          <Done first={done.first} name={name} />
        ) : page === null ? (
          <div className="mt-6" aria-busy="true">
            <Skeleton count={4} height={40} className="mb-3" />
          </div>
        ) : page.state === "closed" ? (
          <p className="mt-6 text-center text-lg" role="status">
            {T.closed(name)}
          </p>
        ) : page.state === "unreachable" ? (
          <div className="mt-6 text-center" role="status">
            <p className="text-lg">{page.message}</p>
            <button type="button" className="mt-4 rounded-lg border-2 border-gray-300 px-6 py-2 font-semibold" onClick={() => load()}>
              {T.again}
            </button>
          </div>
        ) : (
          <form className="mt-6" onSubmit={submit} onFocus={() => track("texts_start", meta())} data-testid="texts-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block font-semibold" htmlFor="texts-first">
                  {T.first}
                </label>
                <input
                  id="texts-first"
                  name="first"
                  className="w-full border-2 p-2 text-base"
                  autoComplete="given-name"
                  maxLength={LIMITS.first}
                  required
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block font-semibold" htmlFor="texts-last">
                  {T.last}
                </label>
                <input
                  id="texts-last"
                  name="last"
                  className="w-full border-2 p-2 text-base"
                  autoComplete="family-name"
                  maxLength={LIMITS.last}
                  required
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-1 block font-semibold" htmlFor="texts-phone">
                {T.phone}
              </label>
              <input
                id="texts-phone"
                name="phone"
                type="tel"
                inputMode="tel"
                className="w-full border-2 p-2 text-base"
                autoComplete="tel"
                placeholder={T.phonePlaceholder}
                maxLength={LIMITS.phone}
                pattern={US_MOBILE_PATTERN}
                title={T.phoneHint}
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            {/* THE ONE BOX: unticked until the client ticks it, its words the door's exactly (what the row keeps), and
                required, because ticking it is the whole of what this page asks. The door checks it again. */}
            <label className="mt-5 flex items-start gap-3 rounded-lg border-2 border-gray-300 p-3" htmlFor="texts-agreed">
              <input
                id="texts-agreed"
                name="agreed"
                type="checkbox"
                className="mt-1 h-5 w-5 shrink-0"
                required
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span className="text-base">{page.words}</span>
            </label>
            <button
              type="submit"
              className={`mt-5 w-full rounded-lg bg-blue-600 py-3 text-lg font-semibold text-white${sending ? " cursor-not-allowed opacity-50" : ""}`}
              disabled={sending}
            >
              {sending ? T.sending : T.button}
            </button>
            {refusal ? (
              <p className="mt-3 text-red-700" role="alert">
                {refusal}
              </p>
            ) : null}
            <p className="mt-4 text-center text-sm text-gray-600">
              <a className="underline" href={SITE_PRIVACY_URL}>
                {T.privacy}
              </a>
              {" · "}
              <a className="underline" href={SITE_TERMS_URL}>
                {T.terms}
              </a>
            </p>
          </form>
        )}
      </main>
    </div>
  );
}

/** After the yes: what the client did and how the texts come, true whatever the row held (texts.ts doneLines). */
function Done({ first, name }: { first: string; name: string }) {
  const { title, lines } = doneLines(first, name);
  return (
    <div className="mt-6 text-center" role="status" data-testid="texts-done">
      <h2 className="text-xl font-bold">{title}</h2>
      {lines.map((line) => (
        <p key={line} className="mt-3 text-base">
          {line}
        </p>
      ))}
    </div>
  );
}
